import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import { toast } from '@/lib/toast'
import { commonQueryKeys } from '@/hooks/useCommon'
const extractErrorMessage = (error: any, fallback: string) => {
  const detail = error?.response?.data?.detail ?? error?.response?.data;
  if (Array.isArray(detail)) {
    // FastAPI/Pydantic validation error list
    const msgs = detail.map((d) => d?.msg || d?.message || JSON.stringify(d));
    return msgs.join(', ');
  }
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    if (typeof detail.message === 'string') return detail.message;
    if (typeof detail.error === 'string') return detail.error;
    try { return JSON.stringify(detail); } catch { /* ignore */ }
  }
  return error?.message || fallback;
}
import type { 
  BusinessProfile, 
  BusinessProfilesResponse, 
  CreateBusinessProfileBody, 
  UpdateBusinessProfileBody 
} from '@/types'

// Query keys
export const businessProfileQueryKeys = {
  all: ['business-profiles'] as const,
  lists: () => [...businessProfileQueryKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...businessProfileQueryKeys.lists(), { filters }] as const,
  details: () => [...businessProfileQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...businessProfileQueryKeys.details(), id] as const,
} as const

// Business profiles query
export const useBusinessProfiles = (options = {}) => {
  return useQuery({
    queryKey: businessProfileQueryKeys.lists(),
    queryFn: async () => {
      const response = await api.get<BusinessProfilesResponse>('/business-profiles/')
      return response.data
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  })
}

// Single business profile query
export const useBusinessProfile = (id: string, options = {}) => {
  return useQuery({
    queryKey: businessProfileQueryKeys.detail(id),
    queryFn: async () => {
      const response = await api.get<BusinessProfile>(`/business-profiles/${id}`)
      return response.data
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  })
}

// Create business profile mutation
export const useCreateBusinessProfile = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: CreateBusinessProfileBody) => {
      const response = await api.post<BusinessProfile>('/business-profiles/', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: businessProfileQueryKeys.lists() })
      // Also invalidate usage query since having a profile changes available quotas
      queryClient.invalidateQueries({ queryKey: commonQueryKeys.usage() })
      toast.success('businessProfile.createdSuccess')
    },
    onError: (error: any) => {
      toast.raw.error(extractErrorMessage(error, 'Failed to create business profile'))
    },
  })
}

// Update business profile mutation
export const useUpdateBusinessProfile = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateBusinessProfileBody }) => {
      const response = await api.patch<BusinessProfile>(`/business-profiles/${id}`, data)
      return response.data
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: businessProfileQueryKeys.lists() })
      queryClient.invalidateQueries({ queryKey: businessProfileQueryKeys.detail(id) })
      toast.success('businessProfile.updatedSuccess')
    },
    onError: (error: any) => {
      toast.raw.error(extractErrorMessage(error, 'Failed to update business profile'))
    },
  })
}

// Delete business profile mutation
export const useDeleteBusinessProfile = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/business-profiles/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: businessProfileQueryKeys.lists() })
      toast.success('businessProfile.deletedSuccess')
    },
    onError: (error: any) => {
      toast.raw.error(error.response?.data?.detail || 'Failed to delete business profile')
    },
  })
}

// Generate business information from website mutation
export const useGenerateBusinessInfoFromWebsite = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ profileId, url }: { profileId: number; url: string }) => {
      // Website scraping can take longer (AI processing), so use a longer timeout
      // Skip global error toast - component will handle errors with inline Alert
      const response = await api.post<{
        raw_context: string;
        description_suggestion: string;
        products_services_suggestion: string;
      }>(`/business-profiles/${profileId}/scrape-website`, { url }, {
        timeout: 60000, // 60 seconds - enough for website scraping and AI processing
        skipGlobalErrorToast: true, // Component handles errors with Alert
      })
      return response.data
    },
    onSuccess: (_, { profileId }) => {
      queryClient.invalidateQueries({ queryKey: businessProfileQueryKeys.detail(profileId.toString()) })
      queryClient.invalidateQueries({ queryKey: commonQueryKeys.usage() })
      toast.success('businessProfile.infoGeneratedSuccess')
    },
  })
}